import format from 'date-fns/format';
import localeCS from 'date-fns/locale/cs';
import localeEN from 'date-fns/locale/en-US';
import {add, differenceInCalendarDays, lastDayOfMonth, parse, parseISO} from "date-fns";

const dateLocales: { [lng: string]: any } = {
    cs: localeCS,
    en: localeEN,
};

let locale: Locale = dateLocales.en;

export function setDateLocale(lng: string) {
    locale = dateLocales[lng] || dateLocales.en;
}

// the generated API types use null for absent dates, so both are accepted here
export function dateToGui(val: Date | string | undefined | null, asFormat?: string) {
    if (!val
        || val === 'Invalid Date'
        || ((val as Date)?.getFullYear && (val as Date)?.getFullYear() <= 1970)) {
        return undefined;
    }
    const v: Date = typeof val === 'string' ? new Date(val) : val;
    // an unparsable string or an absent required date would make format throw
    if (isNaN(v.getTime())) {
        return undefined;
    }
    return format(v, asFormat || 'P', {locale}); // 'd.M.yyyy'
}

/**
 * The API sends plain dates as `YYYY-MM-DD`, which both `new Date(...)` and the
 * generated client parse as **UTC** midnight -- while everything here formats and
 * compares in **local** time. Below UTC that instant falls on the previous
 * calendar day, so the same stored date would mean a different day depending on
 * where the viewer sits.
 *
 * Re-reads the value as a local calendar date, matching how AngularJS handles the
 * same fields through `moment('YYYY-MM-DD')`.
 *
 * Only for date-only fields (swagger `format: date`) -- real timestamps such as
 * `created_at` must keep their local conversion, so do not use it on those.
 */
export function toCalendarDate(val: Date | string | undefined | null): Date | undefined {
    if (!val) {
        return undefined;
    }
    if (typeof val === 'string') {
        const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(val);
        if (parts) {
            return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
        }
        const parsed = new Date(val);
        return isNaN(parsed.getTime()) ? undefined : toCalendarDate(parsed);
    }
    if (isNaN(val.getTime())) {
        return undefined;
    }
    return new Date(val.getUTCFullYear(), val.getUTCMonth(), val.getUTCDate());
}

/**
 * An absent value the generated client declares as required arrives as
 * `new Date(undefined)`, whose getFullYear() is NaN -- so the 1970 guard alone
 * lets it through and `format` then throws. Treated as no value, the same way
 * `toCalendarDate` does it.
 */
const isUsableDate = (val: Date | undefined | null): val is Date =>
    !!val && !isNaN(val.getTime()) && val.getFullYear() > 1970;

export function datetimeToGui(val: Date | undefined | null) {
    if (!isUsableDate(val)) {
        return undefined;
    }
    return format(val, 'P p', {locale}); // 'd.M.yyyy HH:mm'
}

export function datetimeFullToGui(val: Date | undefined) {
    if (!isUsableDate(val)) {
        return undefined;
    }
    return format(val, 'P HH:mm:ss', {locale});
}

export function timeToGui(val: Date | undefined, asFormat?: string) {
    if (!val) {
        return undefined;
    }
    return format(val, asFormat || 'p', {locale}); // 'HH:mm'
}

export function timeVerboseToGui(val: number) {
    if (!val) {
        return undefined;
    }
    return (val > 60
            ? Math.floor(val / 60) + ' h'
            : '0 h')
        + ((val % 60) > 0
            ? ' ' + (val % 60) + ' min'
            : '')
}

export function guiToDatetime(val: Date | string | undefined) {
    if (typeof val === 'string') {
        return parseISO(String(val));
    }
    return val;
}

export function getDayOfWeekName(dow: number) {
    return locale.localize?.day(dow % 7);
}

export function periodToGui(period: string): string {
    if (!period || !period.includes('-')) {
        return period;
    }

    const datum = parse(period, 'yyyy-MM', new Date());

    return format(datum, 'LLLL yyyy', {locale});
}


export function dateDiff(a: Date | string | undefined, b: Date | string | undefined) {
    if (!a || !b) {
        return undefined;
    }
    const da: Date = typeof a === 'string' ? new Date(a) : a;
    const db: Date = typeof b === 'string' ? new Date(b) : b;
    return differenceInCalendarDays(db, da);
}

export function addDuration(date: Date, duration: Duration) {
    return add(date, duration);
}

/** today in the `yyyy-MM-dd` shape a date input expects, in the user's own timezone */
export function todayYmd() {
    return format(new Date(), 'yyyy-MM-dd');
}

export function firstDayOfMonthYmd(date: Date) {
    return format(date, 'yyyy-MM-01');
}

export function lastDayOfMonthYmd(date: Date) {
    return format(lastDayOfMonth(date), 'yyyy-MM-dd');
}

export function addTime(time: string, duration: Duration) {
    return format(add(parse(time, "HH:mm", new Date()), duration), "HH:mm");
}

/**
 * `yyyy-MM-dd` from a date input into the Date the generated client wants for a
 * swagger `format: date` field. The client serialises those with
 * `toISOString().substr(0, 10)`, which reads the date in **UTC** -- a local
 * midnight would travel back a day for anyone east of Greenwich, so the instant
 * has to be built in UTC to survive the round trip. The mirror image of
 * `toCalendarDate`.
 */
export function ymdToApiDate(value: string | undefined | null): Date | undefined {
    const parts = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
    if (!parts) {
        return undefined;
    }
    return new Date(Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])));
}

/** an API date back into the `yyyy-MM-dd` a date input expects */
export function apiDateToYmd(value: Date | string | undefined | null): string {
    const date = toCalendarDate(value);
    return date ? format(date, 'yyyy-MM-dd') : '';
}
